import React, { Key, useEffect, useRef } from 'react';
import frogImageSrc from './assets/frog.png'; // Import the frog image from the assets folder
import handImageSrc from './assets/hand.png';
import backgroundImageSrc from './assets/blueSky.jpg';
import balloonImageSrc from './assets/balloon.png';
import kemtapi from 'skeleton-api';
import { Keypoints } from  'skeleton-api';
import confetti from 'canvas-confetti';
import { drawPoints, drawBone, drawFrogHead, drawHandsAndLegs } from './utils/drawingFunctions'
import {bones} from './utils/drawingFunctions'





const Skeleton: React.FC<{}> = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const frogImageRef = useRef<HTMLImageElement | null>(null);
  const handImageRef = useRef<HTMLImageElement | null>(null);
  const backgroundImageRef = useRef<HTMLImageElement | null>(null);
  const balloonImageRef = useRef<HTMLImageElement | null>(null);
  const balloonVisibleRef = useRef(true);
  const popSoundRef = useRef<HTMLAudioElement | null>(null);
  const faceCanvasRef = useRef<HTMLCanvasElement | null>(null);


  const smoothedFaceRef = useRef({
    centerX: 0,
    centerY: 0,
    scale: 1,
    initialized: false,
  });

  
  
  const updateSkeleton = (kp:Keypoints|null, img : ImageBitmap|ImageData|null) => {
    if (canvasRef.current) {
      drawFrame(canvasRef.current, kp);
    }
  }
  
  
  const init = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false })
    videoRef.current = document.createElement("video");
    videoRef.current.srcObject = stream;
    await videoRef.current.play();
    kemtapi.setInputVideoStream(stream)
    await kemtapi.loadModels()
    const disposer = kemtapi.registerFrameSkeletonCallback(updateSkeleton, "DrawSkeleton");
    return () => {
      disposer();
    }
  }
  
  useEffect(() => {
    init();    
  }, []);
  
  
  
  
  
  // Define the connections between points
  const connections = [
    //["top_of_the_head", "nose"],
    ["nose", "chin"],
    ["chin", "neck"],
    ["neck", "left_shoulder"],
    ["neck", "right_shoulder"],
    ["left_shoulder", "left_elbow"],
    ["left_elbow", "left_wrist"],
    ["right_shoulder", "right_elbow"],
    ["right_elbow", "right_wrist"],
    ["neck", "front_1"],
    ["front_1", "front_2"],
    ["front_2", "front_3"],
    ["front_3", "front_4"],
    ["front_4", "front_5"],
    ["left_hip", "front_5"],
    ["right_hip", "front_5"],
    ["left_hip", "left_knee"],
    ["left_knee", "left_ankle"],
    ["right_hip", "right_knee"],
    ["right_knee", "right_ankle"],
  ];
  
  useEffect(() => {
    const canvas = canvasRef.current;
    const frogImage = new Image();
    const handImage = new Image();
    const backgroundImage = new Image();
    const balloonImage = new Image();
    const popSound = new Audio(require('./assets/pop.mp3'));
    popSoundRef.current = popSound;
    frogImage.src = frogImageSrc; // Use the imported frog image
    handImage.src = handImageSrc;
    backgroundImage.src = backgroundImageSrc;
    balloonImage.src = balloonImageSrc;
    
    
    
    frogImage.onload = () => {
      frogImageRef.current = frogImage; // Store the loaded image
      handImageRef.current = handImage;
      backgroundImageRef.current = backgroundImage;
      balloonImageRef.current = balloonImage;
     
    };
  }, []);
  
  
  
  
  
  const drawFrame = (
    //context: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement,
    frame: Keypoints
  ) => {
    
    const context = canvas.getContext('2d');
    if (!context) return;
    
    context.clearRect(0, 0, context.canvas.width, context.canvas.height);
    
    
    
    // Draw background and balloon only if visible
    drawBackground(context);
    
    if (!frame) return;
    
    drawPoints(context, frame);
    
    
    // drawConnections(context, frame);
    for (const bone of bones) {
      drawBone(context, frame, bone, "#00FF00");
    }
    drawFrogHead(context, frame, frogImageRef.current);
    
    
    drawHandsAndLegs(context, frame, handImageRef.current);

    if (videoRef.current) {
      drawFaceCanvas(frame, videoRef.current);
    }

    
    // Check for collision only if balloon is visible
    if (balloonVisibleRef.current) {
      const leftMiddleFinger = frame.get("left_middle_finger1");
      const rightMiddleFinger = frame.get("right_middle_finger1");
      
      const fingersToCheck = [leftMiddleFinger, rightMiddleFinger];
      
      for (const finger of fingersToCheck) {
        if (finger  && finger.x >= 0 && finger.y >= 0) {
          //const [x, y] = finger;
          if (isTouchingBalloon(finger.x, finger.y) ) {
            balloonVisibleRef.current = false;
            
            const mirroredX = canvas.width - finger.x;
            const canvasRect = canvas.getBoundingClientRect(); 
            const absoluteX = canvasRect.left + mirroredX;
            const absoluteY = canvasRect.top + finger.y  ;

            if (popSoundRef.current) {
              popSoundRef.current.currentTime = 2; // rewind if already played
              popSoundRef.current.play();
            }


            confetti({
              particleCount: 500,
              spread: 200,
              origin: {
                x: absoluteX / window.innerWidth,
                y: absoluteY / window.innerHeight,
              },
            });
            setTimeout(() => {
              balloonVisibleRef.current = true;
            }, 3000);
          }
        }
      }
      
    }
    
  };
  
  const drawFaceCanvas = (frame: Keypoints, video: HTMLVideoElement) => {
    const ctx = faceCanvasRef.current?.getContext("2d");
    if (!ctx || !faceCanvasRef.current) return;

    const topHead = frame.get("top_of_the_head");
    const neck = frame.get("neck");
    const leftEye = frame.get("left_eye");
    const rightEye = frame.get("right_eye");

    if (!topHead || !neck || !leftEye || !rightEye) return;

    const currentFaceHeight = Math.abs(neck.y - topHead.y);
    if (currentFaceHeight < 1) return;

    const rawCenterX = (leftEye.x + rightEye.x) / 2;
    const rawCenterY = topHead.y + currentFaceHeight * 0.45;
    const targetFaceHeight = 120;
    const rawScale = (currentFaceHeight / targetFaceHeight) * 1.0;

    const smoothingFactor = 0.2;
    if (!smoothedFaceRef.current.initialized) {
      smoothedFaceRef.current = {
        centerX: rawCenterX,
        centerY: rawCenterY,
        scale: rawScale,
        initialized: true,
      };
    } else {
      smoothedFaceRef.current.centerX =
        smoothedFaceRef.current.centerX * (1 - smoothingFactor) + rawCenterX * smoothingFactor;
      smoothedFaceRef.current.centerY =
        smoothedFaceRef.current.centerY * (1 - smoothingFactor) + rawCenterY * smoothingFactor;
      smoothedFaceRef.current.scale =
        smoothedFaceRef.current.scale * (1 - smoothingFactor) + rawScale * smoothingFactor;
    }

    const centerX = smoothedFaceRef.current.centerX;
    const centerY = smoothedFaceRef.current.centerY;
    const scale = smoothedFaceRef.current.scale;

    const outputW = 180;
    const outputH = 250;
    const cropW = outputW * scale * 0.5;
    const cropH = outputH * scale * 0.5;
    const cropX = Math.max(0, centerX - cropW / 2);
    const cropY = Math.max(0, centerY - cropH / 2);

    ctx.clearRect(0, 0, faceCanvasRef.current.width, faceCanvasRef.current.height);

    ctx.save();
    ctx.beginPath();
    ctx.ellipse(outputW / 2, outputH / 2, outputW / 2, outputH / 2, 0, 0, Math.PI * 2);
    ctx.clip();

    ctx.drawImage(video, cropX, cropY, cropW, cropH, 0, 0, outputW, outputH);
    ctx.restore();
  };


    const BALLOON_WIDTH = 200;
    const BALLOON_HEIGHT = 200;
    
    const drawBackground = (context: CanvasRenderingContext2D) => {
      const bgImage = backgroundImageRef.current;
      const balloonImage = balloonImageRef.current;
      
      if (bgImage) {
        context.drawImage(bgImage, 0, 0, context.canvas.width, context.canvas.height);
      }
      
      if (balloonImage && balloonVisibleRef.current) {
        const canvasWidth = context.canvas.width;
        const canvasHeight = context.canvas.height;
        const balloonWidth = BALLOON_WIDTH;
        const balloonHeight = BALLOON_HEIGHT;
        const x = canvasWidth - balloonWidth - 30;
        const y = canvasHeight - balloonHeight - 200;
        context.drawImage(balloonImage, x, y, balloonWidth, balloonHeight);
      }
  };
  
  const isTouchingBalloon = (x: number, y: number): boolean => {
    const canvas = canvasRef.current;
    if (!canvas) return false;
    
    const context = canvas.getContext("2d");
    if (!context) return false;

    
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;
    const balloonWidth = BALLOON_WIDTH;
    const balloonHeight = BALLOON_HEIGHT;
    
    const balloonX = canvasWidth - balloonWidth - 30;
    const balloonY = canvasHeight - balloonHeight - 230;
    
    const centerX = balloonX + balloonWidth / 2;
    const centerY = balloonY + balloonHeight / 2;
    const radius = balloonWidth / 2;


    // context.fillStyle = "limegreen";
    // context.beginPath();
    // context.arc(centerX, centerY, 6, 0, 2 * Math.PI);
    // context.fill();
    
    const dx = x - centerX;
    const dy = y - centerY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    
    return distance <= radius;
  };
  
  const EXCLUDED_HAND_LEG_POINTS = new Set([
  // Wrists
  "left_wrist", "right_wrist",

  // Hands - fingers
  "left_thumb1", "left_thumb2", "left_thumb3", "left_thumb4",
  "left_forefinger1", "left_forefinger2", "left_forefinger3", "left_forefinger4",
  "left_middle_finger1", "left_middle_finger2", "left_middle_finger3", "left_middle_finger4",
  "left_ring_finger1", "left_ring_finger2", "left_ring_finger3", "left_ring_finger4",
  "left_pinky_finger1", "left_pinky_finger2", "left_pinky_finger3", "left_pinky_finger4",
  "left_finger", "left_thumb",

  "right_thumb1", "right_thumb2", "right_thumb3", "right_thumb4",
  "right_forefinger1", "right_forefinger2", "right_forefinger3", "right_forefinger4",
  "right_middle_finger1", "right_middle_finger2", "right_middle_finger3", "right_middle_finger4",
  "right_ring_finger1", "right_ring_finger2", "right_ring_finger3", "right_ring_finger4",
  "right_pinky_finger1", "right_pinky_finger2", "right_pinky_finger3", "right_pinky_finger4",
  "right_finger", "right_thumb",

  // Legs
  "left_knee", "right_knee",
  "left_ankle", "right_ankle",
  "left_bigtoe", "right_bigtoe",
  "left_smalltoe", "right_smalltoe",
  "left_heel", "right_heel",
  "LAI", "RAI", "LAF", "RAF", "LAE", "RAE", "LAB", "RAB", "LKI", "RKI", "LKF", "RKF", "LKE", "RKE"
]);

  // const drawPoints = (
  //   context: CanvasRenderingContext2D,
  //   frame: Keypoints|null
  // ) => {
  //   if (!frame) return  ;
    
  //   context.fillStyle = '#006633'; // Set point color
    
  //   for (let key of frame.points()) {
  //     if (EXCLUDED_HAND_LEG_POINTS.has(key)) {
  //       continue; // Skip drawing this point
  //   }
  //     const point = frame.get(key);
  //     if (!point) continue;
      
  //     if (point) {
  //       if (point.x >= 0 && point.y >= 0) {
  //         context.beginPath();
  //         context.arc(point.x, point.y, 5, 0, 2 * Math.PI); // Draw a circle for each point
  //         context.fill();
  //       }
  //     }
  //   }
  // };
  
  // const drawConnections = (
  //   context: CanvasRenderingContext2D,
  //   frame: Keypoints|null
  // ) => {
  //   if (!frame) return;
  //   // Draw connections
  //   context.strokeStyle = '#00FF00'; // Set line color
  //   context.lineWidth = 7; // Set line width
  //   for (const [start, end] of connections) {
  //     const startPoint = frame.get(start);
  //     const endPoint = frame.get(end);
      
  //     if (
  //       startPoint &&
  //       endPoint &&
  //       startPoint.x >= 0 &&
  //       startPoint.y >= 0 &&
  //       endPoint.x >= 0 &&
  //       endPoint.y >= 0
  //     ) {
  //       context.beginPath();
  //       context.moveTo(startPoint.x, startPoint.y); // Move to the starting point
  //       context.lineTo(endPoint.x, endPoint.y); // Draw a line to the endpoint
  //       context.stroke(); // Render the line
  //     }
  //   }
  // }
  
  // const drawFrogHead = (
  //   context: CanvasRenderingContext2D,
  //   frame: Keypoints|null
  // ) => {
  //   if (!frame) return;
    
  //   const nose = frame.get("nose");
  //   const topOfTheHead = frame.get("top_of_the_head");
  //   const image = frogImageRef.current;
    
  //   if (
  //     nose &&
  //     topOfTheHead &&
  //     //nose.length === 2 &&
  //     //topOfTheHead.length === 2 &&
  //     image
  //   ) {
  //     //const [noseX, noseY] = nose;
  //     //const [headX, headY] = topOfTheHead;
      
  //     const dist = Math.sqrt((nose.x-topOfTheHead.x) **2 + (nose.y-topOfTheHead.y) **2);
      
  //     // Calculate the angle between "nose" and "top_of_the_head"
  //     const angle = Math.atan2(topOfTheHead.y - nose.y, topOfTheHead.x - nose.x);
  //     //console.log( "frame " + frameIndex + " angle " + angle);
      
  //     // Set image size
  //     const imageWidth = dist*2;
  //     const imageHeight = dist*2;
      
      
  //     // Rotate and draw the image
  //     context.save(); // Save the current canvas state
  //     context.translate(nose.x, nose.y); // Move the canvas origin to the "nose" position
  //     context.rotate(angle + Math.PI / 2); // Rotate the canvas (add 90 degrees to align the image vertically)
  //     context.drawImage(
  //       image,
  //       -imageWidth / 2, // Center the image
  //       -imageHeight / 2,
  //       imageWidth,
  //       imageHeight
  //     );
  //     context.restore(); // Restore the canvas state
  //   }
  // }
  /*
  const fixPoint = (
  pointName: string,
  frameIndex: number
  ): [number, number] | null => {
  const prevFrameIndex = frameIndex - 1 >= 0 ? frameIndex - 1 : null;
  const nextFrameIndex =
  frameIndex + 1 < data.skeleton.length ? frameIndex + 1 : null;
  
  const prevPoint =
  prevFrameIndex !== null ? data.skeleton[prevFrameIndex][pointName] : null;
  const nextPoint =
  nextFrameIndex !== null ? data.skeleton[nextFrameIndex][pointName] : null;
  
  if (
  prevPoint &&
  nextPoint &&
  prevPoint[0] >= 0 &&
  prevPoint[1] >= 0 &&
  nextPoint[0] >= 0 &&
  nextPoint[1] >= 0
  ) {
  // Calculate average between the two valid points
  const avgX = (prevPoint[0] + nextPoint[0]) / 2;
  const avgY = (prevPoint[1] + nextPoint[1]) / 2;
  return [avgX, avgY];
  }
  return null; // Cannot interpolate
  };
  */
  
  
//   const drawHandsAndLegs = (
//   context: CanvasRenderingContext2D,
//   frame: Keypoints
// ) => {
//   const handConnections: [string, string][] = [
//     ["left_wrist", "left_middle_finger1"],
//     ["right_wrist", "right_middle_finger1"],
//     ["left_ankle", "left_bigtoe"],
//     ["right_ankle", "right_bigtoe"],
//   ];

//   const handImage = handImageRef.current;
//   if (!handImage) return;

//   for (const [start, end] of handConnections) {
//     const startPoint = frame.get(start);
//     const endPoint = frame.get(end);

//     if (!startPoint || !endPoint) continue;

//     // Dynamically compute the image size based on limb length
//     const dx = endPoint.x - startPoint.x;
//     const dy = endPoint.y - startPoint.y;
//     const distance = Math.sqrt(dx * dx + dy * dy);
//     const imageSize = distance * 1.5; 

//     const angle = Math.atan2(dy, dx);

//     context.save();
//     context.translate(startPoint.x, startPoint.y);
//     context.rotate(angle + Math.PI / 2);

//     const adjustedX = -imageSize / 2;
//     const adjustedY = -imageSize;

//     context.drawImage(handImage, adjustedX, adjustedY, imageSize, imageSize);
//     context.restore();
//   }
// };

  
  
  
  return ( 
    <div style={{ width: "78vw", height: "99vh", margin: 0, padding: 0, overflow: "hidden", position: "relative" }}>
      <canvas
        ref={canvasRef}
        width={640}
        height={480}
        style={{
          width: "100%",
          height: "100%",
          transform: "scaleX(-1)",
          display: "block",
        }}
      />
      <canvas
        ref={faceCanvasRef}
        width={180}
        height={250}
        style={{
          position: 'fixed',
          right: '105px',
          top: '550px',
          zIndex: 10,
          borderRadius: '50%',
          overflow: 'hidden',
      }}
      />
  </div>

  );
};

export default Skeleton;